/**
 * 负载均衡器类
 * 负责根据策略选择代理服务
 */

import {
  LoadBalancingAlgorithm,
  ServiceType,
  LoadBalancerStats,
} from "./types";

/**
 * 负载均衡策略配置
 */
const LOAD_BALANCING_CONFIG = {
  // 2025-09-01 20% -> 0%
  // 现有服务承担0%的请求
  // 2025-09-02 0% -> 20%
  EXISTING_SERVICE_WEIGHT: 0.2,
  // 新服务承担100%的请求
  // 2025-09-01 80% -> 100%
  // 2025-09-02 100% -> 80%
  NEW_SERVICE_WEIGHT: 0.8,
  // 负载均衡算法类型
  ALGORITHM: "weighted_random" as const,
} as const;

/**
 * 负载均衡器类
 */
export class LoadBalancer {
  private requestCount = 0;
  private readonly weights = {
    existing: LOAD_BALANCING_CONFIG.EXISTING_SERVICE_WEIGHT,
    new: LOAD_BALANCING_CONFIG.NEW_SERVICE_WEIGHT,
  };

  /**
   * 根据权重随机选择服务
   * @returns 选择的服务类型
   */
  private selectServiceByWeight(): ServiceType {
    const random = Math.random();

    if (random < this.weights.existing) {
      return "existing";
    } else {
      return "new";
    }
  }

  /**
   * 根据请求计数选择服务（轮询方式）
   * @returns 选择的服务类型
   */
  private selectServiceByRoundRobin(): ServiceType {
    this.requestCount++;

    // 每10个请求中，前8个使用现有服务，后2个使用新服务
    if (this.requestCount % 10 < 8) {
      return "existing";
    } else {
      return "new";
    }
  }

  /**
   * 选择代理服务
   * @param algorithm 负载均衡算法
   * @returns 选择的服务类型
   */
  selectService(
    algorithm: LoadBalancingAlgorithm = "weighted_random",
  ): ServiceType {
    switch (algorithm) {
      case "round_robin":
        return this.selectServiceByRoundRobin();
      case "weighted_random":
      default:
        return this.selectServiceByWeight();
    }
  }

  /**
   * 获取服务统计信息
   */
  getStats(): LoadBalancerStats {
    return {
      algorithm: LOAD_BALANCING_CONFIG.ALGORITHM,
      weights: this.weights,
      requestCount: this.requestCount,
    };
  }

  /**
   * 重置请求计数
   */
  resetCount(): void {
    this.requestCount = 0;
  }

  /**
   * 获取配置信息
   */
  getConfig() {
    return {
      ...LOAD_BALANCING_CONFIG,
      weights: this.weights,
    };
  }
}
